import { ref, toValue, watch } from 'vue';
import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { $log } from '@/common/api/log.service';

// High enough that no real account reaches it; a preset list is configuration, not data.
const PRESET_COUNT = 1000;

const nameOf = (preset) => String(preset?.name || '').toLowerCase();

// An _elements read may come back without its resourceType, so only a different type is refused.
const presetsIn = (response) => (Array.isArray(response?.data) ? response.data : []).filter((row) => row && (!row.resourceType || row.resourceType === 'BN_MedicationPreset'));

/**
 * Whether the account has a medication list preset to apply, and the active presets themselves.
 *
 * The check is one id-only read made once, when the caller's gate opens. A failure answers "no
 * presets", so the entry point stays hidden rather than opening onto a dialog that cannot load.
 *
 * @param {{enabled: import('vue').MaybeRefOrGetter<boolean>}} options
 * @returns {{hasActivePresets: import('vue').Ref<boolean>, loadActivePresets: () => Promise<Array<object>>}}
 */
export function useMedicationPresets({ enabled }) {
    const hasActivePresets = ref(false);
    let checked = false;

    const check = async () => {
        checked = true;
        try {
            const response = await $httpFhirApi.get('BN_MedicationPreset?status=active&_count=1&_elements=id');
            hasActivePresets.value = presetsIn(response).length > 0;
        } catch (err) {
            hasActivePresets.value = false;
            $log.warning('useMedicationPresets: could not check for active medication presets', null, err?.message);
        }
    };

    watch(
        () => Boolean(toValue(enabled)),
        (open) => {
            if (open && !checked) check();
        },
        { immediate: true },
    );

    /**
     * Every active preset, sorted by name. Rejects on failure so the caller can say so.
     *
     * @returns {Promise<Array<object>>}
     */
    const loadActivePresets = async () => {
        const response = await $httpFhirApi.get(`BN_MedicationPreset?status=active&_count=${PRESET_COUNT}`);
        const presets = presetsIn(response).sort((a, b) => nameOf(a).localeCompare(nameOf(b)));
        hasActivePresets.value = presets.length > 0;
        return presets;
    };

    return { hasActivePresets, loadActivePresets };
}
